import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectCurrentSession } from "../../features/sessions/sessions-slice";
import { RecurrenceType } from "../../features/sessions/models/RecurrenceType";
import {
  selectTopicChoices,
  selectTopicPlanDraft,
  selectTopicPlanSaveError,
  setTopicPlanDraftTopics,
  startTopicPlanDraft,
} from "../../features/training/training-slice";
import {
  fetchSessionTopicPlan,
  fetchTopicChoices,
} from "../../features/training/slice-extensions/training-topics-slice-extension";
import { TopicRef } from "../../features/training/models/TopicPlan";

interface SessionTopicPlanFieldProps {
  disabled?: boolean;
}

const label = (topic: TopicRef): string => `${topic.code} · ${topic.title}`;

/**
 * The session form's topic rotation: an ordered list of topics for a series (each date
 * takes the next one), a single pick for a one-off. Hidden while the organization has no
 * topics. Edits live in the training slice's draft; the form saves it (saveTopicPlanDraft)
 * after the session save and stays on the page when that fails.
 */
const SessionTopicPlanField: React.FC<SessionTopicPlanFieldProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectCurrentSession);
  const choices = useAppSelector(selectTopicChoices);
  const draft = useAppSelector(selectTopicPlanDraft);
  const saveError = useAppSelector(selectTopicPlanSaveError);
  const sessionId = session?.id ?? null;
  const isOneOff = session?.recurrenceType === RecurrenceType.No;
  const topics = draft?.topics ?? [];

  useEffect(() => {
    dispatch(fetchTopicChoices());
  }, [dispatch]);

  useEffect(() => {
    // A draft whose save failed is kept for its session, so the instructor sees what did not save.
    if (draft?.dirty && draft.sessionId === sessionId) return;
    if (!sessionId) {
      dispatch(startTopicPlanDraft({ sessionId: null, plan: null }));
      return;
    }
    dispatch(fetchSessionTopicPlan(sessionId)).then((result) => {
      const plan = fetchSessionTopicPlan.fulfilled.match(result) ? result.payload : null;
      dispatch(startTopicPlanDraft({ sessionId, plan }));
    });
    // Only a new session id starts a new draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId]);

  if (choices.length === 0 && topics.length === 0) return null;

  const change = (next: TopicRef[]) => {
    dispatch(setTopicPlanDraftTopics(next));
  };
  const pick = (topicId: string): TopicRef | undefined => choices.find((c) => c.topicId === topicId);
  const move = (index: number, by: number) => {
    const next = [...topics];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    change(next);
  };

  return (
    <div className="block text-sm font-medium" data-testid="session-topic-plan">
      <span className="label-text">{t("training.topicPlan.label")}</span>
      <p className="mt-1 text-xs font-normal text-grey-40 dark:text-grey-80">
        {t(isOneOff ? "training.topicPlan.hintOneOff" : "training.topicPlan.hintRotation")}
      </p>

      {isOneOff ? (
        <select
          aria-label={t("training.topicPlan.label")}
          className="input mt-2 w-full sm:w-80"
          value={topics[0]?.topicId ?? ""}
          disabled={disabled}
          onChange={(e) => {
            const topic = pick(e.target.value);
            change(topic ? [topic] : []);
          }}
        >
          <option value="">{t("training.topicPlan.none")}</option>
          {choices.map((c) => (
            <option key={c.topicId} value={c.topicId}>
              {label(c)}
            </option>
          ))}
        </select>
      ) : (
        <>
          {topics.length > 0 && (
            <ol className="mt-2 space-y-1">
              {topics.map((topic, index) => (
                <li
                  key={`${topic.topicId}-${index}`}
                  className="flex items-center gap-2 rounded-lg bg-grey-95 px-3 py-1.5 text-grey-20 dark:bg-primary-20 dark:text-white"
                >
                  <span className="w-6 text-right text-grey-40 dark:text-grey-80">{index + 1}.</span>
                  <span className="min-w-0 flex-1 truncate">{label(topic)}</span>
                  <button type="button" aria-label={t("training.topicPlan.moveUp")} disabled={disabled || index === 0}
                    className="text-grey-40 hover:text-grey-20 disabled:opacity-30 dark:text-grey-80 dark:hover:text-white"
                    onClick={() => move(index, -1)}>
                    <ArrowUp size={16} />
                  </button>
                  <button type="button" aria-label={t("training.topicPlan.moveDown")}
                    disabled={disabled || index === topics.length - 1}
                    className="text-grey-40 hover:text-grey-20 disabled:opacity-30 dark:text-grey-80 dark:hover:text-white"
                    onClick={() => move(index, 1)}>
                    <ArrowDown size={16} />
                  </button>
                  <button type="button" aria-label={t("training.topicPlan.remove")} disabled={disabled}
                    className="text-grey-40 hover:text-grey-20 dark:text-grey-80 dark:hover:text-white"
                    onClick={() => change(topics.filter((_, i) => i !== index))}>
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ol>
          )}
          <select
            aria-label={t("training.topicPlan.add")}
            className="input mt-2 w-full sm:w-80"
            value=""
            disabled={disabled || topics.length >= 40}
            onChange={(e) => {
              const topic = pick(e.target.value);
              if (topic) change([...topics, topic]);
            }}
          >
            <option value="">{t("training.topicPlan.add")}</option>
            {choices.map((c) => (
              <option key={c.topicId} value={c.topicId}>
                {label(c)}
              </option>
            ))}
          </select>
        </>
      )}
      {saveError && (
        <p role="alert" className="mt-2 text-sm font-normal text-red-600 dark:text-red-400">
          {t("training.topicPlan.saveFailed")} {saveError}
        </p>
      )}
    </div>
  );
};

export default SessionTopicPlanField;
